import { useMemo, useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowRight,
  BadgePercent,
  Calculator,
  CheckCircle2,
  IndianRupee,
  Sparkles,
} from 'lucide-react'
import { motion } from 'framer-motion'
import { Container } from '@/components/layout/Container'
import { Button } from '@/components/ui/button'
import { AnimateIn } from '@/components/motion/AnimateIn'
import { SECTION_PY } from '@/lib/layoutSpacing'
import { cn } from '@/lib/utils'
import {
  BROKERAGE_RATE,
  CALCULATOR_DEFAULT,
  CALCULATOR_MAX,
  CALCULATOR_MIN,
  CALCULATOR_STEP,
  calculateBrokerageSavings,
  formatCalculatorAmount,
} from '@/lib/brokerageCalculator'
import { PATHS } from '@/routes/paths'

const HIGHLIGHTS = [
  { text: 'List your property free on QBC', tone: 'primary' },
  { text: 'Connect directly with genuine buyers', tone: 'sky' },
  { text: 'Skip traditional 1–2% brokerage fees', tone: 'emerald' },
  { text: 'Get expert support when you need it', tone: 'violet' },
] as const

const PRESETS = [
  { label: '25L', value: 2_500_000 },
  { label: '50L', value: 5_000_000 },
  { label: '1Cr', value: 10_000_000 },
  { label: '2Cr', value: 20_000_000 },
  { label: '5Cr', value: 50_000_000 },
] as const

const TONE_STYLES = {
  primary: 'border-primary-100 bg-primary-50/70 text-primary-800',
  sky: 'border-sky-100 bg-sky-50/70 text-sky-900',
  emerald: 'border-emerald-100 bg-emerald-50/70 text-emerald-900',
  violet: 'border-violet-100 bg-violet-50/70 text-violet-900',
} as const

export function BrokerageCalculator() {
  const [value, setValue] = useState(CALCULATOR_DEFAULT)

  const savings = useMemo(() => calculateBrokerageSavings(value), [value])
  const traditionalFee = savings
  const percent = Math.round(((value - CALCULATOR_MIN) / (CALCULATOR_MAX - CALCULATOR_MIN)) * 100)
  const rangeRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    rangeRef.current?.style.setProperty('--brokerage-range-fill', `${percent}%`)
  }, [percent])

  return (
    <section className={cn('relative overflow-hidden bg-linear-to-br from-[#eef6ff] via-white to-emerald-50/40', SECTION_PY)}>
      <div
        className="pointer-events-none absolute -left-24 top-0 h-72 w-72 rounded-full bg-primary-200/30 blur-3xl"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -right-16 bottom-0 h-64 w-64 rounded-full bg-emerald-200/25 blur-3xl"
        aria-hidden
      />

      <Container className="relative">
        <AnimateIn>
          <div className="grid items-center gap-4 lg:grid-cols-[1fr_440px] lg:gap-8">
            <div>
              <div className="hidden items-center gap-2 rounded-full border border-primary-200/70 bg-linear-to-r from-primary-500 to-sky-500 px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide text-white shadow-md shadow-primary-500/20 md:inline-flex">
                <Calculator className="h-3.5 w-3.5" />
                Savings Calculator
              </div>

              <h2 className="mt-0 font-display text-xl font-bold tracking-tight text-neutral-900 md:mt-3 md:text-3xl">
                <span className="md:hidden">Brokerage Savings</span>
                <span className="hidden md:inline">
                  Calculate Your Brokerage{' '}
                  <span className="bg-linear-to-r from-primary-600 to-emerald-600 bg-clip-text text-transparent">
                    Savings
                  </span>
                </span>
              </h2>

              <p className="mt-2 hidden max-w-xl text-sm leading-relaxed text-neutral-600 sm:text-[15px] md:block">
                Estimate how much brokerage you can save by listing on QBC and connecting directly
                with buyers — no middlemen, no hidden charges.
              </p>

              <div className="mt-5 hidden gap-2.5 sm:grid-cols-2 md:grid">
                {HIGHLIGHTS.map((item) => (
                  <div
                    key={item.text}
                    className={cn(
                      'flex items-start gap-2 rounded-xl border px-3 py-2.5 text-sm leading-snug',
                      TONE_STYLES[item.tone],
                    )}
                  >
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 opacity-80" />
                    {item.text}
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div
                className="pointer-events-none absolute -inset-px rounded-2xl bg-linear-to-br from-primary-300/40 via-sky-200/30 to-emerald-200/40 blur-sm"
                aria-hidden
              />

              <div className="relative overflow-hidden rounded-2xl border border-white/80 bg-white/95 p-3.5 shadow-[0_12px_40px_rgba(15,93,154,0.12)] backdrop-blur-sm sm:p-5">
                <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-primary-100/50 blur-2xl" aria-hidden />

                <div className="relative flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-neutral-500 sm:text-xs">
                      Estimated Property Value
                    </p>
                    <motion.p
                      key={value}
                      initial={{ opacity: 0.6, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2 }}
                      className="mt-0.5 font-display text-xl font-bold text-primary-700 sm:mt-1 sm:text-[1.75rem]"
                    >
                      {formatCalculatorAmount(value)}
                    </motion.p>
                  </div>
                  <div className="hidden h-10 w-10 items-center justify-center rounded-xl bg-linear-to-br from-primary-500 to-sky-500 text-white shadow-md shadow-primary-500/25 sm:flex">
                    <IndianRupee className="h-5 w-5" />
                  </div>
                </div>

                <div className="relative mt-4">
                  <input
                    ref={rangeRef}
                    type="range"
                    min={CALCULATOR_MIN}
                    max={CALCULATOR_MAX}
                    step={CALCULATOR_STEP}
                    value={value}
                    onChange={(event) => setValue(Number(event.target.value))}
                    aria-label="Estimated property value"
                    className="brokerage-range h-2.5 w-full cursor-pointer appearance-none rounded-full bg-neutral-200 accent-primary-500"
                  />
                  <div className="mt-1.5 flex justify-between text-[10px] font-semibold text-neutral-400">
                    <span>₹ 0</span>
                    <span>₹ 5 Cr+</span>
                  </div>
                </div>

                <div className="mt-3 hidden flex-wrap gap-1.5 sm:flex">
                  {PRESETS.map((preset) => (
                    <button
                      key={preset.label}
                      type="button"
                      onClick={() => setValue(preset.value)}
                      className={cn(
                        'rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors',
                        value === preset.value
                          ? 'border-primary-500 bg-primary-500 text-white shadow-sm'
                          : 'border-neutral-200 bg-neutral-50 text-neutral-600 hover:border-primary-200 hover:bg-primary-50 hover:text-primary-700',
                      )}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>

                <div className="mt-3 sm:mt-4">
                  <div className="hidden grid-cols-2 gap-2.5 sm:grid">
                    <div className="rounded-xl border border-neutral-200/80 bg-neutral-50/80 px-3 py-2.5">
                      <p className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
                        <BadgePercent className="h-3 w-3" />
                        Traditional
                      </p>
                      <p className="mt-1 text-sm font-bold text-neutral-700 line-through decoration-red-400/70">
                        {formatCalculatorAmount(traditionalFee)}
                      </p>
                      <p className="mt-0.5 text-[10px] text-neutral-400">{(BROKERAGE_RATE * 100).toFixed(1)}% brokerage</p>
                    </div>

                    <div className="rounded-xl border border-emerald-200/80 bg-linear-to-br from-emerald-50 to-primary-50/60 px-3 py-2.5">
                      <p className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                        <Sparkles className="h-3 w-3" />
                        You Save
                      </p>
                      <motion.p
                        key={savings}
                        initial={{ opacity: 0.6, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.2 }}
                        className="mt-1 font-display text-lg font-bold text-emerald-700"
                      >
                        {formatCalculatorAmount(savings)}
                      </motion.p>
                      <p className="mt-0.5 text-[10px] text-emerald-600/80">With QBC listing</p>
                    </div>
                  </div>

                  <div className="rounded-xl border border-emerald-200/80 bg-linear-to-br from-emerald-50 to-primary-50/60 px-3 py-2.5 sm:hidden">
                    <p className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                      <Sparkles className="h-3 w-3" />
                      You Save
                    </p>
                    <motion.p
                      key={savings}
                      initial={{ opacity: 0.6, scale: 0.98 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.2 }}
                      className="mt-1 font-display text-xl font-bold text-emerald-700"
                    >
                      {formatCalculatorAmount(savings)}
                    </motion.p>
                  </div>
                </div>

                <Button
                  asChild
                  className="mt-3 h-10 w-full bg-linear-to-r from-primary-600 to-primary-500 text-sm text-white shadow-md shadow-primary-500/25 hover:from-primary-700 hover:to-primary-600 sm:mt-4 sm:h-11"
                >
                  <Link to={PATHS.contact}>
                    Book Free Valuation
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>

                <p className="mt-2 hidden text-center text-[11px] text-neutral-400 sm:block">
                  Free consultation · No obligation · Gujarat properties
                </p>
              </div>
            </div>
          </div>
        </AnimateIn>
      </Container>
    </section>
  )
}
